// The Body at Work (Taylor's v4.1 doc, Oct 22, 2026): the nine v4 gifts on one cycle, and a
// person's pairing, their top two gifts, read from where the two sit on it.
//
// Nothing here changes scoring. A pairing is worked out from a stored v4 output record (the gift
// ranks and raw totals, the office patterns and the quadrant), so every v4 result already has
// one. Pure functions; the results page, the team view and the MCP all call the same ones.

import { PAIRING_COPY, type PairingCopy } from "./pairingCopy.js";
import type { DomainCode } from "./types.js";

/** Clockwise from the top: See it (Vision), Gather it (Gathering), Sustain it (Sustaining). */
export const CYCLE = ["Prophecy", "Pioneering", "Teaching", "Outreach", "Exhorting", "Leading", "Giving", "Serving", "Mercy"] as const;
export type CycleGift = (typeof CYCLE)[number];

export type Arc = "Vision" | "Gathering" | "Sustaining";
export const ARCS: { arc: Arc; gifts: CycleGift[] }[] = [
  { arc: "Vision", gifts: ["Prophecy", "Pioneering", "Teaching"] },
  { arc: "Gathering", gifts: ["Outreach", "Exhorting", "Leading"] },
  { arc: "Sustaining", gifts: ["Giving", "Serving", "Mercy"] },
];

/** Each gift's step on the cycle (Taylor's words). */
export const CYCLE_STEPS: Record<CycleGift, string> = {
  Prophecy: "sees what's needed",
  Pioneering: "starts it",
  Teaching: "makes it clear",
  Outreach: "gathers people",
  Exhorting: "moves them forward",
  Leading: "orders the work",
  Giving: "supplies it",
  Serving: "does it",
  Mercy: "cares for people",
};

export const arcOf = (gift: CycleGift): Arc => ARCS.find((a) => a.gifts.includes(gift))!.arc;

const position = (gift: string): number => {
  const i = (CYCLE as readonly string[]).indexOf(gift);
  if (i < 0) throw new Error(`Not a v4 gift: ${gift}`);
  return i;
};

/** Steps between two gifts the short way around: 1 (neighbours) to 4 (across). */
export function cycleDistance(a: string, b: string): number {
  const d = Math.abs(position(a) - position(b)) % CYCLE.length;
  return Math.min(d, CYCLE.length - d);
}

export type PairType = "Flow" | "Span" | "Bridge";
/** Flow: neighbours. Span: two or three apart. Bridge: across (four). */
export const pairType = (distance: number): PairType => (distance <= 1 ? "Flow" : distance >= 4 ? "Bridge" : "Span");

export const PAIR_TYPE_LINES: Record<PairType, string> = {
  Flow: "Deep momentum in one stretch of the work, with one clear blind spot straight across.",
  Span: "Covers a wider stretch of the work, with a smaller blind spot on the far side.",
  Bridge: "Translates between distant parts of the work. No single blind spot; the risk is the stretch in between.",
};

/**
 * The blind spot: whichever gift or gifts sit farthest from both of the pair, read as the
 * largest shortest-distance to either of the two. Structural, never a score: it names the
 * people they most need beside them, not something they lack. In cycle order.
 */
export function blindSpotOf(a: string, b: string): CycleGift[] {
  const others = CYCLE.filter((g) => g !== a && g !== b);
  const reach = others.map((g) => ({ g, d: Math.min(cycleDistance(g, a), cycleDistance(g, b)) }));
  const far = Math.max(...reach.map((r) => r.d));
  return reach.filter((r) => r.d === far).map((r) => r.g);
}

/** The six pairings that echo an Ephesians 4 office (only shown when that office pattern shows too). */
const OFFICE_ECHOES: { gifts: [CycleGift, CycleGift]; office: string }[] = [
  { gifts: ["Pioneering", "Leading"], office: "Apostle" },
  { gifts: ["Outreach", "Exhorting"], office: "Evangelist" },
  { gifts: ["Leading", "Mercy"], office: "Shepherd" },
  { gifts: ["Prophecy", "Exhorting"], office: "Prophet" },
  { gifts: ["Prophecy", "Teaching"], office: "Teacher" },
  { gifts: ["Pioneering", "Teaching"], office: "Teacher" },
];

const samePair = (x: readonly string[], y: readonly string[]) => (x[0] === y[0] && x[1] === y[1]) || (x[0] === y[1] && x[1] === y[0]);

export const officeEchoOf = (a: string, b: string): string | null => OFFICE_ECHOES.find((e) => samePair(e.gifts, [a, b]))?.office ?? null;

export function pairingCopyOf(a: string, b: string): PairingCopy {
  const copy = PAIRING_COPY.find((p) => samePair(p.gifts, [a, b]));
  if (!copy) throw new Error(`No pairing copy for ${a} + ${b}`);
  return copy;
}

/** A stable key: the two gifts in cycle order ("Pioneering+Teaching"). */
export const pairingKey = (a: string, b: string): string => [a, b].sort((x, y) => position(x) - position(y)).join("+");

export interface Pairing {
  key: string;
  /** The two gifts, rank 1 first. */
  gifts: [CycleGift, CycleGift];
  name: string;
  type: PairType;
  distance: number;
  oneLine: string;
  craves: string;
  crushedBy: string;
  bound: string;
  blindSpot: CycleGift[];
  /** The office this pairing echoes, only when the person's own office pattern for it shows. */
  officeEcho: string | null;
  /** Their second and third gifts are one most/least step apart (raw totals 1 or less). */
  closeCall: { third: CycleGift; otherName: string } | null;
  /** Caged and Fragmented also see the bound form (rule 3). */
  showBound: boolean;
}

/** What a pairing needs from a v4 output record. */
export interface PairingInput {
  gifts: { gift: string; total: number; rank: number }[];
  office_patterns: { pattern: string }[];
  quadrant: string;
}

/** The person's pairing: their top two gifts by the stored ranking and its tie-break. */
export function pairingFor(record: PairingInput): Pairing {
  const ranked = [...record.gifts].sort((x, y) => x.rank - y.rank);
  const [first, second, third] = ranked as [PairingInput["gifts"][number], PairingInput["gifts"][number], PairingInput["gifts"][number] | undefined];
  const a = first.gift as CycleGift;
  const b = second.gift as CycleGift;
  const copy = pairingCopyOf(a, b);
  const distance = cycleDistance(a, b);
  const echo = officeEchoOf(a, b);
  return {
    key: pairingKey(a, b),
    gifts: [a, b],
    name: copy.name,
    type: pairType(distance),
    distance,
    oneLine: copy.oneLine,
    craves: copy.craves,
    crushedBy: copy.crushedBy,
    bound: copy.bound,
    blindSpot: blindSpotOf(a, b),
    officeEcho: echo && record.office_patterns.some((p) => p.pattern === echo) ? echo : null,
    // "Within 17 points (one most/least step)": compared on the raw 0-6 totals, so rounding the
    // 0-100 scores can't decide it.
    closeCall: third && second.total - third.total <= 1 ? { third: third.gift as CycleGift, otherName: pairingCopyOf(a, third.gift).name } : null,
    showBound: record.quadrant === "Caged" || record.quadrant === "Fragmented",
  };
}

// ---------------------------------------------------------------------------
// A team on the cycle (team scorecard)
// ---------------------------------------------------------------------------

export interface TeamMemberPairing {
  firstName: string;
  pairing: Pairing;
  /** Their home base, for partner suggestions (a tie goes to the teammate in their opposite domain). */
  home: DomainCode;
}

export interface TeamCycle {
  /** How many people carry each gift in their pairing. */
  nodes: { gift: CycleGift; people: number }[];
  /** Each distinct pairing on the team, with how many people share it. */
  lines: { key: string; gifts: [CycleGift, CycleGift]; name: string; type: PairType; people: string[] }[];
  /** Each arc's share of the team's 2 x members picks. */
  arcs: { arc: Arc; picks: number; of: number }[];
  insights: { kind: "breaks" | "thin_arc" | "flow_cluster" | "bridges" | "partner"; title: string; body: string }[];
}

const OPPOSITE: Record<DomainCode, DomainCode> = { STR: "INF", INF: "STR", REL: "EXE", EXE: "REL" };
const list = (xs: string[]) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} and ${xs.at(-1)}`);

/**
 * The "Team on the cycle" view and its insight rules (v4.1 doc). Pairings and blind spots are
 * gift-based, so they're shown by first name as gifts already are; bound forms never appear.
 */
export function teamCycle(members: TeamMemberPairing[]): TeamCycle {
  const nodes = CYCLE.map((gift) => ({ gift, people: members.filter((m) => m.pairing.gifts.includes(gift)).length }));
  const byKey = new Map<string, TeamCycle["lines"][number]>();
  for (const m of members) {
    const line = byKey.get(m.pairing.key) ?? { key: m.pairing.key, gifts: m.pairing.gifts, name: m.pairing.name, type: m.pairing.type, people: [] };
    line.people.push(m.firstName);
    byKey.set(m.pairing.key, line);
  }
  const lines = [...byKey.values()];
  const of = members.length * 2;
  const arcs = ARCS.map(({ arc, gifts }) => ({ arc, picks: nodes.filter((n) => gifts.includes(n.gift)).reduce((s, n) => s + n.people, 0), of }));
  const insights: TeamCycle["insights"] = [];
  if (members.length === 0) return { nodes, lines, arcs, insights };

  // The cycle breaks: a gift nobody carries.
  const empty = nodes.filter((n) => n.people === 0).map((n) => n.gift);
  for (const gift of empty) {
    const before = CYCLE[(position(gift) + CYCLE.length - 1) % CYCLE.length]!;
    insights.push({
      kind: "breaks",
      title: `${gift} is empty`,
      body: `No one carries ${gift}, so the cycle breaks after ${before} (${CYCLE_STEPS[before]}).`,
    });
  }
  // A thin arc: under half the picks of the strongest arc.
  const strongest = Math.max(...arcs.map((a) => a.picks));
  for (const a of arcs) {
    if (strongest > 0 && a.picks < strongest / 2) {
      insights.push({ kind: "thin_arc", title: `Thin in ${a.arc}`, body: `${a.arc} has ${a.picks} of ${a.of} picks, under half of the team's strongest arc.` });
    }
  }
  // A shared flow cluster: three or more people share one flow pairing.
  for (const line of lines) {
    if (line.type !== "Flow" || line.people.length < 3) continue;
    const blind = blindSpotOf(...line.gifts);
    const compounds = blind.filter((g) => empty.includes(g));
    insights.push({
      kind: "flow_cluster",
      title: `${line.people.length} share ${line.name}`,
      body: `${list(line.people)} share a ${line.gifts.join(" and ")} flow pair: deep strength in one stretch of the work, and a shared blind spot (${list(blind)}).${
        compounds.length ? ` Nobody here carries ${list(compounds)} either, so the gap compounds.` : ""
      }`,
    });
  }
  // Bridges.
  const bridges = members.filter((m) => m.pairing.type === "Bridge").map((m) => m.firstName);
  if (bridges.length) {
    insights.push({ kind: "bridges", title: bridges.length === 1 ? "One bridge" : `${bridges.length} bridges`, body: `${list(bridges)} ${bridges.length === 1 ? "translates" : "translate"} across the cycle. Use them to connect clusters.` });
  }
  // Partner suggestions: a teammate whose pairing covers one of their blind-spot gifts; ties go
  // to the teammate in their opposite domain.
  for (const m of members) {
    const covers = members.filter((o) => o !== m && o.pairing.gifts.some((g) => m.pairing.blindSpot.includes(g)));
    if (!covers.length) continue;
    const best = covers.find((o) => o.home === OPPOSITE[m.home]) ?? covers[0]!;
    const gift = best.pairing.gifts.find((g) => m.pairing.blindSpot.includes(g))!;
    insights.push({ kind: "partner", title: `Pair ${m.firstName} with ${best.firstName}`, body: `${best.firstName}'s ${gift} covers ${m.firstName}'s blind spot.` });
  }
  return { nodes, lines, arcs, insights };
}
